import { useState, useEffect } from "react";
import api from "@/lib/api";
import { useTheme } from "next-themes";
import { F, th as palette, Btn } from "@/lib/bricks";
import { fmtDay } from "@/lib/dates";
import { Page, PageHeader, Stats, Section, List, Row, Empty, Loading, humanize } from "@/components/calm/Calm";
import { toast } from "sonner";

export default function SoloBillingPage() {
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const { resolvedTheme } = useTheme();
  const dark = resolvedTheme === 'dark';
  const p = palette(dark);

  useEffect(() => {
    api.get("/billing/overview")
      .then(r => { setBilling(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const openPortal = async () => {
    setActionLoading("portal");
    try {
      const { data } = await api.post("/billing/portal", { return_url: window.location.href });
      if (data.url) window.location.href = data.url;
      else toast.error("Could not open billing portal");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not open billing portal");
    } finally {
      setActionLoading(null);
    }
  };

  const handleSwitch = async (plan) => {
    if (actionLoading) return;
    const onFree = !(billing.base_charge > 0);
    // Clay Free → paid: no Stripe subscription yet — use checkout for that package
    if (onFree && plan.amount > 0) {
      setActionLoading(plan.key);
      try {
        const { data } = await api.post("/billing/checkout", {
          package_id: plan.key,
          origin_url: window.location.origin,
        });
        if (data.url) window.location.href = data.url;
        else toast.error("Checkout failed");
      } catch (err) {
        toast.error(err.response?.data?.detail || "Checkout failed");
      } finally {
        setActionLoading(null);
      }
      return;
    }
    // Paid subscriber (or switch to free): Stripe Customer Portal for plan changes
    await openPortal();
  };

  if (loading) return <Loading />;
  if (!billing) return (
    <Page testid="solo-billing-page" width={960}>
      <PageHeader title="Billing" help="plans-billing" />
      <Empty>Billing details aren't available right now. Try again in a moment.</Empty>
    </Page>
  );

  const overageCost = billing.overage_cost || 0;
  const projected = billing.projected_total || billing.base_charge || 0;
  const plans = billing.available_plans || [];
  const invoices = billing.invoices || [];
  const money = (n) => `$${(n || 0).toLocaleString()}`;
  const rowLink = { background:'none', border:'none', padding:'0 6px', minHeight:44, minWidth:44, cursor: actionLoading ? 'wait' : 'pointer', fontFamily:F.display, fontSize:13, color:p.ink2, textDecoration:'underline', textUnderlineOffset:3, textDecorationColor:p.hairS, whiteSpace:'nowrap', opacity: actionLoading ? 0.6 : 1 };

  const onFree = !(billing.base_charge > 0);
  const upgradeTo = onFree ? plans.find(pl => !pl.active && pl.amount > 0) : null;
  const mainAction = upgradeTo
    ? <Btn dark={dark} data-testid="billing-main" disabled={!!actionLoading} onClick={() => handleSwitch(upgradeTo)}>{actionLoading === upgradeTo.key ? 'Opening…' : `Upgrade to ${upgradeTo.name || 'a paid plan'}`}</Btn>
    : onFree ? null
    : <Btn dark={dark} data-testid="billing-main" disabled={!!actionLoading} onClick={openPortal}>{actionLoading === 'portal' ? 'Opening…' : 'Manage billing'}</Btn>;

  return (
    <Page testid="solo-billing-page" width={960}>
      <PageHeader title="Billing" help="plans-billing" subtitle="Solo plan, billed monthly." action={mainAction} />

      <Stats items={[
        { label: billing.plan_name || 'Clay', value: money(billing.base_charge) },
        { label: 'Next charge', value: money(projected) },
        overageCost > 0 ? { label: 'Overage this cycle', value: money(overageCost), attention: true } : null,
      ]} />

      <Section title="Plans" note={billing.plan_description || 'Media booked separately.'}>
        <List testid="solo-billing-plans">
          {plans.map(plan => (
            <Row key={plan.key} testid={`plan-${plan.key}`}
              title={plan.name}
              meta={[{ text: `${money(plan.amount)}/mo`, figure: true }, plan.description || 'Media booked separately.']}
              status={plan.active ? 'Current plan' : null}
              aside={plan.active ? null : <button style={rowLink} onClick={() => handleSwitch(plan)}>{actionLoading === plan.key ? 'Opening…' : 'Switch'}</button>} />
          ))}
        </List>
      </Section>

      <Section title="Invoices">
        {invoices.length === 0 ? (
          <Empty>No invoices yet. Your first one appears after your first billing cycle.</Empty>
        ) : (
          <List testid="solo-billing-invoices">
            {invoices.map((inv, i) => (
              <Row key={inv.invoice_id || i}
                title={inv.description || `${billing.plan_name} cycle`}
                meta={[inv.invoice_id ? { text: inv.invoice_id, figure: true } : null, inv.created_at ? { text: fmtDay(inv.created_at), figure: true } : null, { text: money(inv.amount), figure: true }]}
                status={humanize(inv.status || 'pending')}
                attention={inv.status === 'failed' || inv.status === 'past_due'} />
            ))}
          </List>
        )}
      </Section>
    </Page>
  );
}
